import { ProgramType } from '@newfront-insurance/account-api';
import { useAnalytics, BadRequestError } from '@newfront-insurance/data-layer-client';
import type { RouterMutationOutput } from '@newfront-insurance/data-layer-server';
import type { DomainObjectIndex } from '@newfront-insurance/dod-api';
import { BLOCKED_FILE_EXTENSIONS, BLOCKED_MIME_TYPES } from '@newfront-insurance/files-api';
import type {
  CreateFolderCommand,
  FileCreateResponse,
  FileGroup,
  FolderCommandDetails,
  TriageStatus,
} from '@newfront-insurance/files-api';
import { ObjectReferenceTypeEnum } from '@newfront-insurance/search-api';
import axios, { isAxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { useState } from 'react';

import { useAdminTRPC } from '../../../shared/providers/trpc';
import type { SharedRouter } from '../../../shared/trpc/router';
import { ObjectReferenceType } from '../../types';
import { FolderTypes } from '../features/folders-tree/helpers/types';
import { isFolderAssociationData } from '../helpers';
import type { UploadFile } from '../types';
/**
 * Validates file extensions and MIME types to prevent dangerous file uploads
 */
function validateFile(file: File): { isValid: boolean; error?: string } {
  const { name, type: contentType } = file;
  const filename = name.toLowerCase();

  // Check for blocked file extensions (case insensitive)
  const problematicExt = BLOCKED_FILE_EXTENSIONS.find((ext) => filename.endsWith(ext));
  if (problematicExt) {
    return {
      isValid: false,
      error: `File extension '${problematicExt.substring(1)}' is not allowed`,
    };
  }

  // Check for blocked MIME types
  if (contentType && BLOCKED_MIME_TYPES.includes(contentType)) {
    return {
      isValid: false,
      error: `Content type '${contentType}' is not allowed`,
    };
  }

  return { isValid: true };
}

interface UseUploadFilesOptions {
  onSuccess?: (
    newFiles: RouterMutationOutput<SharedRouter, 'files.commitFiles'>,
    newFoldersData: FolderCommandDetails<CreateFolderCommand>[],
  ) => void | Promise<void>;
}

interface UploadFileOptions extends DomainObjectIndex<string> {
  fileOptions?: {
    group?: FileGroup;
  };
}

interface UploadProps {
  files: UploadFile[];
  uploadOptions?: UploadFileOptions;
  skipEntityChain?: boolean;
  programType?: ProgramType;
  isEBAssociationEnabled?: boolean;
  shouldHideFromClient?: boolean;
  deletable?: boolean;
  triageStatus?: TriageStatus;
  sscCaseLink?: string;
  ownerUuids?: string[];
  /**
   * You need to pass the accountUuid if you want to enable folder associations for cases where the file is not associated with a client
   */
  accountUuid?: string;
}

export function useUploadFiles(options?: UseUploadFilesOptions) {
  const { useMutation } = useAdminTRPC();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const { mutateAsync: createFiles } = useMutation('files.createFiles');
  const { mutateAsync: commitFiles } = useMutation('files.commitFiles');
  const { mutateAsync: archiveFile } = useMutation('files.archiveFile');
  const { mutateAsync: createFolder } = useMutation('files.createFolder');
  const analytics = useAnalytics();

  const upload = async ({
    // 1. create files
    // 2. takes signed urls and upload contents to each one
    // 3. commit files

    files,
    uploadOptions,
    skipEntityChain = false,
    /**
     * Determines the client visibility of the uploaded files.
     * Usually a file that is uploaded by servicing team is not visible to clients,
     * however EB files(belonging to an EB account) uploaded by servicing team will have client visibility.
     */
    programType = ProgramType.PROPERTY_AND_CASUALTY,
    isEBAssociationEnabled = false,
    shouldHideFromClient = false,
    deletable = false,
    accountUuid,
  }: UploadProps) => {
    // Validate all files before processing
    const invalidFiles = files
      .map((file) => ({ file, validation: validateFile(file.rawFile) }))
      .filter(({ validation }) => !validation.isValid)
      .map(({ file, validation }) => ({
        fileName: file.rawFile.name,
        error: validation.error!,
      }));

    if (invalidFiles.length > 0) {
      const errorMessage = invalidFiles.map((file) => `${file.fileName}: ${file.error}`).join('; ');
      throw new BadRequestError(`Invalid files detected: ${errorMessage}`, { invalidFiles });
    }

    const newFoldersData: FolderCommandDetails<CreateFolderCommand>[] = [];

    const filesWithFormattedAssociations = await Promise.all(
      files.map(async (file) => {
        const folderAssociations = file.associations?.filter(isFolderAssociationData);

        // We should not need to create folders in this hook anymore.
        // Folders are eagerly created so that they can be reused by multi file upload
        // Ticket to track this - https://newfront.atlassian.net/browse/BP-1702
        const hasNewFoldersToCreate = file.newFolders && file.newFolders.length > 0;
        if (hasNewFoldersToCreate && !accountUuid) {
          throw new Error('accountUuid is required to associate files to client');
        }

        const newFoldersUuids: string[] = [];
        if (hasNewFoldersToCreate) {
          // create all new folders for the file
          await Promise.all(
            file.newFolders!.map(async (newFolder) => {
              if (hasNewFoldersToCreate) {
                // if folder name is not provided, it is a year folder
                if (!newFolder.folderName) {
                  await Promise.all(
                    newFolder.associatedYears.map(async (associatedYear) => {
                      const newFolderResponse = await createFolder({
                        name: associatedYear,
                        parentFolderType: newFolder.parentFolderType,
                        associatedYears: [associatedYear],
                        accountUuid: accountUuid!,
                        displayName: associatedYear,
                      });
                      newFoldersUuids.push(newFolderResponse.objectUuid!);
                      newFoldersData.push(newFolderResponse);
                    }),
                  );
                } else {
                  const newFolderResponse = await createFolder({
                    name: newFolder.folderName,
                    parentFolderType: newFolder.parentFolderType,
                    associatedYears: newFolder.associatedYears,
                    accountUuid: accountUuid!,
                    displayName: newFolder.folderName,
                  });
                  newFoldersUuids.push(newFolderResponse.objectUuid!);
                  newFoldersData.push(newFolderResponse);
                }
              }
            }),
          );
        }

        // convert folder associations to object associations
        const folderObjectAssociations =
          folderAssociations?.map((association) => ({
            id: association.id as string,
            type: ObjectReferenceTypeEnum.FOLDER,
            isPrimary: true,
          })) ?? [];

        // include the new folders in the folder object associations
        folderObjectAssociations.push(
          ...newFoldersUuids.map((uuid) => ({
            id: uuid,
            type: ObjectReferenceTypeEnum.FOLDER,
            isPrimary: true,
          })),
        );

        // include folder associations in the entity associations moving forward
        const entityAssociations = file.associations?.filter((association) => !isFolderAssociationData(association));
        const formattedFile = {
          ...file,
          associations: [
            ...(entityAssociations?.map((association) => ({
              id: association.id as string,
              type: association.type,
            })) ?? []),
            ...folderObjectAssociations,
          ],
        };

        return formattedFile;
      }),
    );

    // if upload options refers to a subfolder, we also need to make them point to the client
    let checkedUploadOptions = uploadOptions;
    if (checkedUploadOptions?.type === FolderTypes.SUBFOLDER) {
      checkedUploadOptions = {
        ...checkedUploadOptions,
        id: accountUuid!,
        type: 'client',
      };
    }

    // Create files in the database
    const newFiles = await createFiles({
      objectId: checkedUploadOptions?.id,
      objectType: checkedUploadOptions?.type,
      group: checkedUploadOptions?.fileOptions?.group,
      files: filesWithFormattedAssociations.map((uploadFile) => ({
        filename: uploadFile.rawFile.name,
        displayName: uploadFile.displayName,
        category: uploadFile.category,
        summary: uploadFile.summary,
        contentType: uploadFile.rawFile.type,
        contentLength: uploadFile.rawFile.size.toString(),
        clientNotes: uploadFile.clientNotes,
        programTypes: uploadFile.programTypes,
        associations: uploadFile.associations,
        shouldSendToClient: uploadFile.shouldSendToClient,
        triageStatus: uploadFile.triageStatus,
        sscCaseLink: uploadFile.sscCaseLink,
        ownerUuids: uploadFile.ownerUuids,
      })),
      skipEntityChain,
      programType,
      isEBAssociationEnabled,
      shouldHideFromClient,
      deletable,
    });

    newFiles.forEach((file: FileCreateResponse) => {
      const { objectRefs, id: fileId } = file.file;
      analytics.track({
        event: 'Files improvements - upload with associations',
        properties: {
          fileId,
          entityAssociations: objectRefs,
          entityAssociationsCount: objectRefs?.length ?? 0,
          hasAccount: !!(
            (objectRefs?.[ObjectReferenceType.ACCOUNT] && objectRefs?.[ObjectReferenceType.ACCOUNT]?.length > 0) ||
            objectRefs?.[ObjectReferenceType.CLIENT]?.length
          ),
          hasInvoice: !!(
            objectRefs?.[ObjectReferenceType.INVOICE] && objectRefs?.[ObjectReferenceType.INVOICE]?.length > 0
          ),
          programType,
        },
      });
    });

    // Upload each file
    try {
      await Promise.all(
        newFiles.map(async (file, index) => {
          if (!file.uploadUrl) {
            return null;
          }

          const axiosInstance = axios.create({});

          axiosRetry(axiosInstance, {
            retries: 3,
            retryDelay: (retryCount) => retryCount * 1000,
          });

          await axiosInstance.request({
            method: 'PUT',
            url: file.uploadUrl,
            headers: {
              'Content-Type': file.file.contentType ?? 'application/octet-stream',
            },
            data: files[index].rawFile,
            onUploadProgress: (p) => {
              if (setUploadProgress) {
                setUploadProgress((prev) => ({
                  ...prev,
                  [`${files[index].rawFile.name}-${files[index].rawFile.type}`]: (p.loaded / (p.total ?? 1)) * 100,
                }));
              }
            },
            withCredentials: false,
          });
        }),
      );
    } catch (error) {
      if (isAxiosError(error)) {
        // eslint-disable-next-line no-console
        console.error(`[S3 Upload Error]`, {
          details: error.toJSON(),
          status: error.response?.status,
          statusText: error.response?.statusText,
        });
      } else {
        // eslint-disable-next-line no-console
        console.error(`[S3 Upload Error]`, error);
      }
      // archive files if there was an error uploading them
      await Promise.all(newFiles.map((file) => archiveFile({ fileUuid: file.file.id })));
      // throw the error so that we can log it with analytics
      throw error;
    }
    setUploadProgress({});

    const response = await commitFiles({
      files: newFiles.map((file) => file.file.id),
    });

    // We should remove the new folders data from the response since its eagerly created- https://newfront.atlassian.net/browse/BP-1702
    options?.onSuccess?.(response, newFoldersData);
    return response;
  };

  return {
    upload,
    uploadProgress,
  };
}
